import { CardShell, EmptyState } from '@atlas/ui-components'
import { Button, buttonVariants, cn, Skeleton } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'
import { ArticleBody } from '@/components/articles/ArticleBody'
import type { Article } from '@/domain/article.types'
import { relativeTime } from '@/lib/labels'
import { isEmptyRichText } from '@/lib/rich-text'

/**
 * The Brand details tab's body - presentational, so every state can be seen in
 * isolation and the tab itself only has to resolve the article.
 *
 * The article renders through `ArticleBody`, the same read path the composer's
 * canvas uses, which is what makes a reader see exactly what the author produced.
 * Nothing here is sample data: a brand with no article says so.
 */

export interface BrandDetailsPanelProps {
	/** The brand's article, or `null` when it has none. */
	article: Article | null
	brandName: string
	isLoading?: boolean
	/** Why the read failed, or `null` when it did not. */
	error?: string | null
	onRetry: () => void
	/** The composer link the empty state offers, seeded with this brand + the template. */
	createTo: string
}

/**
 * The frame the error state uses. The empty state composes EmptyState instead:
 * a failed read is not an empty collection, so the two must not share a shape
 * that offers "write the first one".
 */
function StatePanel({ children }: { children: React.ReactNode }) {
	return <CardShell className="border-dashed p-10 text-center">{children}</CardShell>
}

/** This panel's display-scale emphasis, layered over the shared EmptyState frame. */
const PANEL_HEADING = 'font-ds-display text-ds-xl text-ds-text'
const PANEL_BODY = 'mx-auto block max-w-md text-ds-sm text-ds-text-muted'

/** The panel's shape while the article is being read, so the article lands where it stood. */
function LoadingPanel() {
	return (
		<CardShell aria-busy="true" className="p-6">
			<span className="sr-only">Loading the brand details…</span>
			<Skeleton shape="text" className="h-7 w-1/3" />
			<Skeleton shape="text" className="mt-4 h-4 w-full" />
			<Skeleton shape="text" className="mt-2 h-4 w-5/6" />
			<Skeleton shape="text" className="mt-2 h-4 w-2/3" />
		</CardShell>
	)
}

export function BrandDetailsPanel({ article, brandName, isLoading, error, onRetry, createTo }: BrandDetailsPanelProps) {
	if (isLoading) return <LoadingPanel />

	// Failed, not empty: nothing is known about this brand's articles, so the only
	// honest offer is another attempt - never an invitation to write one that may
	// already exist.
	if (error) {
		return (
			<StatePanel>
				<p className="font-ds-display text-xl text-ds-text">Could not load the brand details</p>
				<p className="mx-auto mt-2 max-w-md text-sm text-ds-text-muted">{error}</p>
				<Button variant="secondary" className="mt-5 rounded-ds-full px-5" onPress={onRetry}>
					Try again
				</Button>
			</StatePanel>
		)
	}

	if (!article) {
		return (
			<EmptyState
				className="p-10"
				title={<span className={PANEL_HEADING}>No brand details yet</span>}
				description={
					<span className={PANEL_BODY}>
						{brandName} has no brand-details article. One holds the brand's voice and its colour palettes, and is
						classified to the brand like every other object.
					</span>
				}
				action={
					/* A link, not a button: opening the composer is navigation, and a reader
					   should be able to open it in its own tab. */
					<Link to={createTo} className={cn(buttonVariants({ size: 'sm' }), 'rounded-ds-full px-5')}>
						Write brand details
					</Link>
				}
			/>
		)
	}

	return (
		<CardShell as="article" className="p-6">
			<header className="mb-6 border-b border-ds-border pb-4">
				<h2 className="font-ds-display text-2xl font-semibold leading-snug text-ds-text">{article.title}</h2>
				<p className="mt-2 font-mono text-ds-2xs uppercase tracking-ds-wider text-ds-text-subtle">
					Updated {relativeTime(article.updatedAt)}
				</p>
			</header>
			{isEmptyRichText(article.content) ? (
				<p className="text-sm text-ds-text-muted">This article has no content yet.</p>
			) : (
				<ArticleBody html={article.content} />
			)}
		</CardShell>
	)
}
